import { NgIf } from "@angular/common";
import { Component, ChangeDetectionStrategy } from "@angular/core";
import { RouterLink } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { take, catchError, of, finalize, Observable, map, BehaviorSubject } from "rxjs";
import { CustomizerSettingsService } from "../../../customizer-settings/customizer-settings.service";
import { BaseSearchOptions } from "../../../models/core/table/BaseSearchOptions";
import { FilterDataType } from "../../../models/core/table/FilterDataType";
import { QueryBuilderParams } from "../../../models/core/table/QueryBuilderParams";
import { TableActionButton } from "../../../models/core/table/TableActionButton";
import { TableColumn } from "../../../models/core/table/TableColumn";
import { TableEvent } from "../../../models/core/table/TableEvent";
import { TableSearchEventData } from "../../../models/core/table/TableSearchEventData";
import { Tenant } from "../../../models/domain/Tenant";
import { EventTypes } from "../../../models/enums/EventTypes";
import { GroupType } from "../../../models/enums/GroupType";
import { ModalText } from "../../../models/enums/ModalText";
import { LoadingService } from "../../../services/loading.service";
import { ModalService } from "../../../services/modal.service";
import { RoleService } from "../../../services/roleService";
import { getSearchOperator } from "../../../utilities/common-functions";
import { TenantService } from "../../../services/tenant.service";
import { B2BClientDetailsComponent } from "../b2bclient-details/b2bclient-details.component";
import { TableComponent } from "../../../core/table/table.component";
import { AuthenticationService } from "../../../services/authentication.service";
import { Address } from "../../../models/domain/Address";
import { RoleEnum } from "../../../models/enums/RoleEnum";
import { CreateB2BClient } from "../../../models/domain/B2BClient/CreateB2BClient";
import { TableCellTypeEnum } from "../../../models/enums/TableCellTypeEnum";

@Component({
    selector: 'app-b2bclient-list',
    imports: [RouterLink, TableComponent, B2BClientDetailsComponent, NgIf],
    templateUrl: './b2bclient-list.component.html',
    styleUrl: './b2bclient-list.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class B2BClientListComponent {

    //#region Table Variables
    public columns!: TableColumn[];
    public actions!: TableActionButton[];
    public totalRecords = 0;
    public pageIndex = 0;
    public pageSize = 10;
    public pageSizeOptions: number[] = [10, 50, 100];
    public searchOptions!: BaseSearchOptions;
    //#endregion

    userId = 0;
    createb2bclient!: CreateB2BClient;
    //createb2bclients$: Observable<CreateB2BClient[]> = new Observable<CreateB2BClient[]>();
       public createb2bclientsSubject = new BehaviorSubject<CreateB2BClient[]>(
        []
    );
    createb2bclients$ = this.createb2bclientsSubject.asObservable();

    // Popup Trigger
    classApplied = false;
    isToggled = false;
    showModal = false;
    deleteModalData!: Tenant;
    ConfirmText = ModalText.ConfirmYes
    CancelText = ModalText.ConfirmNo

    // Image modal
    showImageModal = false;
    selectedImageUrl = '';
    selectedImageTitle = '';

    constructor(
        public themeService: CustomizerSettingsService,
        public tenantService: TenantService,
        private modalService: ModalService,
        private toasterService: ToastrService,
        private roleService: RoleService,
        private loadingService: LoadingService,
        private authService: AuthenticationService,

    ) {
        this.initialize();
    }

    ngOnInit(): void {
        this.getData();
    }

    private initialize(): void {
        this.themeService.isToggled$.subscribe(isToggled => {
            this.isToggled = isToggled;
        });

        this.searchOptions = new BaseSearchOptions(undefined, 'id', 0, this.pageIndex, this.pageSize);
        this.buildTable();
    }

    private getData(): void {
        
        this.loadingService.showLoading();
   
       this.authService
           .getB2bClient(this.searchOptions)
           .pipe(
               take(1),
             map((result) => {  
                
                const body = result.body ?? { values: [], totalCount: 0 };
                this.totalRecords = body.totalCount; // keep total count

                body.values.forEach((item) => {
                    
                    item.address = item.address ?? new Address();
                });
                 this.createb2bclientsSubject.next(body.values); // return only data for the table
            }),
            catchError((error) => {
                if (error && error.error) {
                    if (error.error.isCustom) {
                        this.toasterService.warning(error.error.message);
                    } else {
                        this.toasterService.error('Something went wrong!');
                    }
                } else {
                    this.toasterService.error('Something went wrong!');
                }
                 this.totalRecords = 0;
                 this.createb2bclientsSubject.next([]); // clear the table
                return of(null); // empty array for the table
            }),
            finalize(() => {
                this.loadingService.hideLoading();
            }),
        ).subscribe();
    }

    public buildTable(): void {
                this.columns = [
                    new TableColumn(
                        'Client.Name',
                        ' ClientName',
                        FilterDataType.String,
                        true,
                        false,
                        (data: CreateB2BClient) => `${data?.clientName}`,
                        true,
                        { tooltip: 'CreateB2BClient clientName' }
                    ),
                    new TableColumn(
                        'Tenant.Name',
                        ' TenantName',
                        FilterDataType.String,
                        true,
                        false,
                        (data: CreateB2BClient) => `${data?.tenantName}`,
                        true,
                        { tooltip: 'CreateB2BClient tenantName' }
                    ),
                    new TableColumn(
                        'Tenant.Logo',
                        'Logo',
                        FilterDataType.String,
                        false,
                        false,
                        (data: CreateB2BClient) => `${data?.logoUrl}`,
                        false,
                        {
                            tooltip: 'Logo',
                            styleExpression: 'padding:5px',
                            onImageClick: (event: MouseEvent, data: CreateB2BClient) => this.onImageClick(event, data)
                        },
                        TableCellTypeEnum.IMAGE
                    ),
                    
                    new TableColumn(
                        'firstName',
                        ' FirstName',
                        FilterDataType.String,
                        true,
                        false,
                        (data: CreateB2BClient) => `${data?.firstName}`,
                        true,
                        { tooltip: 'CreateB2BClient firstName' }
                    ),
                    new TableColumn(
                        'lastName',
                        ' LastName',
                        FilterDataType.String,
                        true,
                        false,
                        (data: CreateB2BClient) => `${data?.lastName}`,
                        true,
                        { tooltip: 'CreateB2BClient lastName' }
                    ),
                    new TableColumn(
                        'email',
                        ' Email',
                        FilterDataType.String,
                        true,
                        false,
                        (data: CreateB2BClient) => `${data?.email}`,
                        true,
                        { tooltip: 'CreateB2BClient email' }
                    ),
                    new TableColumn(
                        'phone',
                        ' Phone',
                        FilterDataType.String,
                        true,
                        false,
                        (data: CreateB2BClient) => `${data?.phone}`,
                        true,
                        { tooltip: 'CreateB2BClient phone' }
                    ),
                    new TableColumn(
                        'Tenant.Address.AddressLine1',
                        ' AddressLine1',
                        FilterDataType.String,
                        true,
                        false,
                        (data: CreateB2BClient) => `${data?.address?.addressLine1}`,
                        true,
                        { tooltip: 'CreateB2BClient addressLine1' }
                    ),
                    new TableColumn(
                        'Tenant.Address.AddressLine2',
                        ' AddressLine2',
                        FilterDataType.String,
                        true,
                        false,
                        (data: CreateB2BClient) => `${data?.address?.addressLine2}`,
                        true,
                        { tooltip: 'CreateB2BClient addressLine2' }
                    ),
                    new TableColumn(
                        'Tenant.Address.PinCode',
                        ' PinCode',
                        FilterDataType.String,
                        true,
                        false,
                        (data: CreateB2BClient) => `${data?.address?.pinCode}`,
                        true,
                        { tooltip: 'CreateB2BClient pinCode' }
                    ),
                    new TableColumn(
                        'Tenant.Address.State.Name',
                        ' State',
                        FilterDataType.String,
                        true,
                        false,
                        (data: CreateB2BClient) => `${data?.address?.state?.name}`,
                        true,
                        { tooltip: 'CreateB2BClient state' }
                    ),
                    new TableColumn(
                        'Tenant.Address.City.Name',
                        ' City',
                        FilterDataType.String,
                        true,
                        false,
                        (data: CreateB2BClient) => `${data?.address?.city?.name}`,
                        true,
                        { tooltip: 'CreateB2BClient city' }
                    ),
                ];

        this.actions = [];
        if (this.authService.hasRoles([RoleEnum.ProductMaster])) {
            this.actions.push(
                new TableActionButton("edit", (event, value) => this.onEditClick(event, value), (value) => this.onDisabledClick(), "Edit", "Edit"
            ),
               new TableActionButton("delete", (event, value) => this.onDeleteClick(event, value), (value) => this.onDisabledClick(), "Delete", "Delete"
            )
        );   
        }
    }
    
    public onAddClick(): void {
        this.createb2bclient = new CreateB2BClient();
        this.toggleClass();
    }

    public onEditClick(event: MouseEvent, data: CreateB2BClient): void {
        event.stopPropagation();
        this.createb2bclient = data;
        this.toggleClass();
    }

    public onDeleteClick(event: MouseEvent, data: CreateB2BClient): void {
        
        event.stopPropagation();
        this.modalService.openModal(
            'Delete Confirmation',
            `Are you sure you want to delete <strong> ${data.clientName} </strong> ?`,
            ModalText.ConfirmYes,
            ModalText.ConfirmNo
        );

        this.modalService.onConfirm.subscribe((isConfirm) => {
            if (isConfirm && data.id) {
                this.loadingService.showLoading();
                this.authService.deleteB2bClient(data.id).pipe(
                    take(1),
                    catchError((error) => {
                        if (error && error.error) {
                            if (error.error.isCustom) {
                                this.toasterService.warning(error.error.message);
                            } else {
                                this.toasterService.error('Something went wrong!');
                            }
                        } else {
                            this.toasterService.error('Something went wrong!');
                        }
                        return of(null);
                    })
                ).subscribe(result => {
                    if (result?.body) {
                        this.toasterService.info("Record delete successfully.");
                        this.getData();
                    }
                    this.loadingService.hideLoading();
                });
            }
        });
    }

    public onDisabledClick(): boolean {
        return false;
    }

    public tableEvent(event: TableEvent<any>): void {
        if (event.type === EventTypes.TABLE) {
            const eventValue = event.data as TableSearchEventData;

            this.searchOptions.updateByObject(eventValue.baseSearchOptions);

            if (eventValue.columnFilterValues) {
                this.createFilter(eventValue);
            }
            else {
                this.searchOptions.filter = '';
            }
            //this.getData();
        }
        if (event.type === EventTypes.CLEAR) {
            this.clearFilters();
            //this.getData();
        }
        if (event.type === EventTypes.IMAGECLICK) {
            const { row } = event.data;
            this.onImageClick(event.data.event, row);
        }
    }

    public onImageClick(_event: MouseEvent, data: CreateB2BClient): void {
        this.selectedImageUrl = data?.logoUrl ?? '';
        this.selectedImageTitle = `${data?.clientName} - ${data?.tenantName}`;
        this.showImageModal = true;
    }

    public closeImageModal(): void {
        this.showImageModal = false;
        this.selectedImageUrl = '';
        this.selectedImageTitle = '';
    }

    public clearFilters(): void {
        this.pageIndex = 0;
        this.pageSize = 10;
        this.searchOptions = new BaseSearchOptions(undefined, 'id', 0, this.pageIndex, this.pageSize);
    }

    private createFilter(eventValue: TableSearchEventData): void {
        const queryParams: QueryBuilderParams[] = [];

        for (const [columnName, { dataType, opType, searchValue }] of Object.entries(eventValue.columnFilterValues)) {
            const searchOperator = getSearchOperator(opType.toString());
            if (!searchOperator) continue; // Skip if no valid operator

            queryParams.push({
                fieldName: columnName, // Adjust as necessary for your field mapping
                dataType: dataType,
                searchOperator,
                searchValue: searchValue,
                groupType: GroupType.OR
            });
        }

        this.searchOptions.filter = this.searchOptions.generateQuery(queryParams);
    }

    public toggleClass(): void {
        this.classApplied = !this.classApplied;
    }

    public classAppliedEvent(flag: boolean): void {
        this.classApplied = flag;
    }

    public saveConfirmationEvent(isSaved: boolean): void {
        if (isSaved) {
            this.getData();
        }
    }
}